import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Contact, FileSignature, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { SelectionBar } from '../components/SelectionBar';
import { DataTable, type Column } from '../components/DataTable';
import { HelpHint } from '../components/HelpHint';
import { useSession } from '../lib/session';
import type { CustomerRow, Plan } from '../lib/types';

const kes = (amount: number): string => `KES ${amount.toLocaleString('en-KE')}`;

/**
 * Customers, and the two things you start from a customer: a contract, and a
 * login.
 *
 * Both live here rather than on the Users page or the Contracts page, because
 * both only make sense with a customer already in hand — a client account with
 * no customer behind it can see nothing, and a contract needs somebody to be
 * for.
 */
export function CustomersPage() {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [drafting, setDrafting] = useState<CustomerRow | null>(null);
  const navigate = useNavigate();
  const { can } = useSession();

  const customers = useQuery({
    queryKey: ['customers', 'list', q, page],
    queryFn: () => api.customerList({ q: q || undefined, page }),
    /*
     * Hold the previous page while the next one loads. Without this the table
     * empties and the pager jumps on every click, which reads as the list being
     * broken rather than as it fetching.
     */
    placeholderData: (previous) => previous,
  });

  /*
   * Searching restarts at page one. Staying on page nine of the old result
   * while the new one has two pages shows an empty table and a pager that
   * disagrees with it.
   */
  const search = (value: string) => {
    setQ(value);
    setPage(1);
  };

  const columns: Column<CustomerRow>[] = [
    {
      key: 'name',
      sortBy: (c) => c.name.toLowerCase(),
      header: 'Customer',
      cell: (customer) => (
        <div className="min-w-0">
          <p className="font-medium">{customer.name}</p>
          <p className="text-xs text-muted-foreground sm:hidden">{customer.phoneE164}</p>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      secondary: true,
      sortBy: (c) => c.phoneE164,
      cell: (c) => c.phoneE164,
    },
    {
      key: 'type',
      sortBy: (c) => c.type,
      header: 'Type',
      secondary: true,
      cell: (c) => (c.type === 'business' ? 'Business' : 'Home'),
    },
    {
      key: 'account',
      sortBy: (c) => c.accountRef ?? null,
      header: 'Account',
      secondary: true,
      cell: (c) => c.accountRef ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'email',
      sortBy: (c) => c.email ?? null,
      header: 'Email',
      secondary: true,
      cell: (c) => c.email ?? <span className="text-muted-foreground">no email</span>,
    },
    {
      key: 'sites',
      header: 'Sites',
      numeric: true,
      secondary: true,
      sortBy: (c) => c.siteCount,
      cell: (c) => c.siteCount,
    },
    {
      key: 'contracts',
      header: 'Contracts',
      numeric: true,
      sortBy: (c) => c.contractCount,
      cell: (c) => c.contractCount,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      cell: (customer) =>
        can('contract:draft') ? (
          <button
            type="button"
            className="btn-quiet text-xs"
            onClick={(event) => {
              // The row itself is not clickable here, but stopping this keeps
              // the button honest if it ever becomes so.
              event.stopPropagation();
              setDrafting(drafting?.id === customer.id ? null : customer);
            }}
          >
            <FileSignature className="h-4 w-4" aria-hidden="true" />
            New contract
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Customers</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Who we serve, what they have agreed to, and who can sign in.
          </p>
        </div>
        {/*
          Orange moves rather than multiplying. While the form is closed this is
          the screen's single action; once it opens, the form's Save is, and this
          steps back to an outline. Jua allows exactly one orange element, and
          two of them competing is worse than either.
        */}
        {can('customer:write') && (
          <button
            type="button"
            className={adding ? 'btn-secondary' : 'btn-primary'}
            onClick={() => setAdding((v) => !v)}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add a customer
          </button>
        )}
      </div>

      {adding && <NewCustomerForm onDone={() => setAdding(false)} />}

      {/* Once, on the page. This briefly lived inside the customer card, where
          it repeated itself down the whole list. */}

      <input
        type="search"
        className="input max-w-sm"
        placeholder="Name, phone or account number"
        value={q}
        onChange={(event) => search(event.target.value)}
      />

      {customers.isPending ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : (
        <>
        {/*
          Export only. There is no bulk action on a customer that is safe to
          offer from a list: archiving one hides their contracts and their
          history, and doing it to forty at once by mistake is not something
          an undo button makes better.
        */}
        <SelectionBar
          rows={customers.data?.customers ?? []}
          selected={selected}
          onClear={() => setSelected(new Set())}
          rowKey={(customer) => customer.id}
          exportName="customers"
          csv={[
            { header: 'Account', value: (c) => c.accountRef },
            { header: 'Name', value: (c) => c.name },
            { header: 'Phone', value: (c) => c.phoneE164 },
            { header: 'Email', value: (c) => c.email },
            { header: 'Type', value: (c) => c.type },
            { header: 'Sites', value: (c) => c.siteCount },
          ]}
        />

        <DataTable
          selected={selected}
          onSelect={setSelected}
          rows={customers.data?.customers ?? []}
          columns={columns}
          rowKey={(customer) => customer.id}
          caption="Customers"
          page={
            customers.data
              ? {
                  page: customers.data.page,
                  perPage: customers.data.perPage,
                  total: customers.data.total,
                  pages: customers.data.pages,
                }
              : undefined
          }
          onPage={setPage}
          empty={
            <EmptyState
              icon={Contact}
              headline={q ? 'Nobody matches that' : 'No customers yet'}
              body={
                q
                  ? 'Try a phone number, or part of a name. Search covers the account number too.'
                  : 'Everyone you connect appears here, with their agreements and their contracts.'
              }
              action={q ? { label: 'Clear the search', onClick: () => search('') } : undefined}
            />
          }
        />
        </>
      )}

      {drafting && (
        <NewContractForm
          customerId={drafting.id}
          onDone={(id) => {
            setDrafting(null);
            if (id) navigate(`/contracts/${id}`);
          }}
        />
      )}
    </div>
  );
}

function NewCustomerForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', phone: '', email: '', accountRef: '' });

  const create = useMutation({
    mutationFn: () =>
      api.createCustomer({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        accountRef: form.accountRef || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      onDone();
    },
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <section className="card">
      <h3 className="mb-1 font-heading text-base">Add a customer</h3>
      <p className="mb-3 text-sm text-muted-foreground">
        They get a welcome email with their referral code, on the address you give here.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium">Name</span>
          <input className="input mt-1" value={form.name} onChange={set('name')} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Phone</span>
          <input
            className="input mt-1"
            value={form.phone}
            onChange={set('phone')}
            placeholder="0712 345 678"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Email</span>
          <input className="input mt-1" value={form.email} onChange={set('email')} />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Account number (optional)</span>
          <input className="input mt-1" value={form.accountRef} onChange={set('accountRef')} />
        </label>
      </div>

      {create.isError && (
        <p className="mt-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
          {(create.error as ApiError).message}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={create.isPending || form.name.length < 2 || form.phone.length < 6}
          onClick={() => create.mutate()}
        >
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </section>
  );
}

function NewContractForm({
  customerId,
  onDone,
}: {
  customerId: string;
  onDone: (contractId?: string) => void;
}) {
  const [packageCode, setPackageCode] = useState('');
  const [termMonths, setTermMonths] = useState(12);

  const reference = useQuery({
    queryKey: ['contracts', 'reference'],
    queryFn: api.contractReferenceData,
    staleTime: 300_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.createContract({
        customerId,
        packageCode: packageCode || undefined,
        termMonths,
        effectiveFrom: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: (contract) => onDone(contract.id),
  });

  const plans: Plan[] = reference.data?.plans ?? [];

  return (
    <div className="mt-3 rounded-xl border border-border p-4">
      <h4 className="flex items-center gap-1.5 font-heading text-sm">
        New contract
        <HelpHint>
          The price fills in from the published list, and you can change it on the draft.
          Nothing is sent until you press Send on the contract itself.
        </HelpHint>
      </h4>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium">Package</span>
          <select
            className="input mt-1"
            value={packageCode}
            onChange={(event) => setPackageCode(event.target.value)}
          >
            <option value="">Choose one</option>
            {plans.map((plan) => (
              <option key={plan.code} value={plan.code}>
                {plan.name} — {plan.speedMbps} Mbps — {kes(plan.monthlyFeeKes)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">Term (months)</span>
          <select
            className="input mt-1"
            value={termMonths}
            onChange={(event) => setTermMonths(Number(event.target.value))}
          >
            {[1, 3, 6, 12, 24, 36].map((months) => (
              <option key={months} value={months}>
                {months}
              </option>
            ))}
          </select>
        </label>
      </div>

      {create.isError && (
        <p className="mt-3 text-sm text-danger-700">{(create.error as ApiError).message}</p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={create.isPending || !packageCode}
          onClick={() => create.mutate()}
        >
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Create the draft
        </button>
        <button type="button" className="btn-secondary text-sm" onClick={() => onDone()}>
          Cancel
        </button>
      </div>
    </div>
  );
}
